import * as decoding from 'lib0/decoding';
import * as encoding from 'lib0/encoding';
import { type Awareness, removeAwarenessStates } from 'y-protocols/awareness';

/**
 * A person whose tab has been out of sight this long no longer takes part for
 * the others. Browsers slow the timers of a tab hidden for five minutes, and a
 * state renewed that rarely comes and goes at its receivers; the limit lies
 * below that point.
 */
export const AWAY_AFTER_HIDDEN_MS = 270_000;

const HIDDEN_SINCE_FIELD = 'hiddenSince';

function hiddenSinceOf(state: unknown): number | undefined {
  if (!state || typeof state !== 'object' || !(HIDDEN_SINCE_FIELD in state))
    return undefined;
  const since = state[HIDDEN_SINCE_FIELD];
  return typeof since === 'number' && Number.isFinite(since)
    ? since
    : undefined;
}

interface HiddenClient {
  since: number;
  leavesAt: number;
  timer?: ReturnType<typeof setTimeout>;
}

export interface HiddenClientTracking {
  /**
   * Leaves the states of away clients out of an incoming awareness update, so
   * a late renewal from a hidden tab does not bring its person back.
   */
  withoutAwayClients(update: Uint8Array): Uint8Array;
  stop(): void;
}

/**
 * Publishes since when the own tab is out of sight, and takes every other
 * client out of the awareness at the moment it becomes away. The moment comes
 * from the sender, so someone joining later sees the same people; a sender
 * clock ahead of the own one counts from first receipt. `origin` marks the
 * removals as not to be sent on.
 */
export function trackHiddenClients(
  awareness: Awareness,
  origin: string
): HiddenClientTracking {
  const hiddenClients = new Map<number, HiddenClient>();

  const hiddenClientOf = (clientId: number, since: number) => {
    const known = hiddenClients.get(clientId);
    if (known?.since === since) return known;
    if (known?.timer !== undefined) clearTimeout(known.timer);
    const hiddenClient: HiddenClient = {
      since,
      leavesAt: Math.min(since, Date.now()) + AWAY_AFTER_HIDDEN_MS,
    };
    hiddenClients.set(clientId, hiddenClient);
    return hiddenClient;
  };

  const forget = (clientId: number) => {
    const known = hiddenClients.get(clientId);
    if (known?.timer !== undefined) clearTimeout(known.timer);
    hiddenClients.delete(clientId);
  };

  const watch = (clientIds: Iterable<number>) => {
    for (const clientId of clientIds) {
      if (clientId === awareness.clientID) continue;
      const state = awareness.getStates().get(clientId);
      if (!state) {
        // Someone taken out as away stays known, so their renewals stay out.
        const known = hiddenClients.get(clientId);
        if (known && Date.now() < known.leavesAt) forget(clientId);
        continue;
      }
      const since = hiddenSinceOf(state);
      if (since === undefined) {
        forget(clientId);
        continue;
      }
      const hiddenClient = hiddenClientOf(clientId, since);
      if (hiddenClient.timer !== undefined) clearTimeout(hiddenClient.timer);
      hiddenClient.timer = setTimeout(
        () => {
          hiddenClient.timer = undefined;
          removeAwarenessStates(awareness, [clientId], origin);
        },
        Math.max(0, hiddenClient.leavesAt - Date.now())
      );
    }
  };

  const publishOwnVisibility = () => {
    const hidden = document.visibilityState === 'hidden';
    // Timers of a hidden tab run late; whoever became away meanwhile leaves now.
    if (!hidden) watch(awareness.getStates().keys());
    const local = awareness.getLocalState();
    if (!local) return;
    const published = hiddenSinceOf(local) !== undefined;
    if (hidden === published) return;
    awareness.setLocalStateField(
      HIDDEN_SINCE_FIELD,
      hidden ? Date.now() : undefined
    );
  };
  const hasPage = typeof document !== 'undefined';
  if (hasPage) {
    document.addEventListener('visibilitychange', publishOwnVisibility);
    publishOwnVisibility();
  }

  const handleChange = (
    changes: Record<'added' | 'updated' | 'removed', number[]>
  ) => {
    // A tab opened out of sight gets its state after the last visibility event.
    if (hasPage && changes.added.includes(awareness.clientID))
      publishOwnVisibility();
    watch([...changes.added, ...changes.updated, ...changes.removed]);
  };
  awareness.on('change', handleChange);
  watch(awareness.getStates().keys());

  return {
    withoutAwayClients(update) {
      const decoder = decoding.createDecoder(update);
      const count = decoding.readVarUint(decoder);
      const kept: Array<[clientId: number, clock: number, state: string]> = [];
      for (let index = 0; index < count; index++) {
        const clientId = decoding.readVarUint(decoder);
        const clock = decoding.readVarUint(decoder);
        const state = decoding.readVarString(decoder);
        // Cursor updates arrive per frame; only a hidden tab's state is parsed.
        const since =
          clientId !== awareness.clientID && state.includes(HIDDEN_SINCE_FIELD)
            ? hiddenSinceOf(JSON.parse(state))
            : undefined;
        if (
          since !== undefined &&
          Date.now() >= hiddenClientOf(clientId, since).leavesAt
        )
          continue;
        kept.push([clientId, clock, state]);
      }
      if (kept.length === count) return update;
      const encoder = encoding.createEncoder();
      encoding.writeVarUint(encoder, kept.length);
      for (const [clientId, clock, state] of kept) {
        encoding.writeVarUint(encoder, clientId);
        encoding.writeVarUint(encoder, clock);
        encoding.writeVarString(encoder, state);
      }
      return encoding.toUint8Array(encoder);
    },
    stop() {
      awareness.off('change', handleChange);
      for (const clientId of [...hiddenClients.keys()]) forget(clientId);
      if (hasPage)
        document.removeEventListener('visibilitychange', publishOwnVisibility);
    },
  };
}
